import { msg } from "@lingui/core/macro";
import {
  type NavigateOptions,
  type RegisteredRouter,
  type RoutePaths,
  useNavigate,
} from "@tanstack/solid-router";
import { TodoDetail } from "./todo-detail";
import { TodoDetailSkeleton } from "./todo-detail-skeleton";
import { Transition } from "solid-transition-group";
import {
  batch,
  createEffect,
  createSignal,
  For,
  onCleanup,
  onMount,
  Show,
  Suspense,
  untrack,
} from "solid-js";
import { Portal } from "solid-js/web";
import { X } from "lucide-solid";
import { useI18n } from "@/providers/i18n-provider";
import { useDrizzle } from "@/hooks/use-drizzle";
import { updateTodoMutation } from "@listto/shared";
import { useDrawerSwipeNav } from "../hooks/use-drawer-swipe-nav";
import { useTodoDetailPrefetch } from "../hooks/use-todo-detail-prefetch";
import { COMMAND_IDS } from "@/lib/shortcuts/defaults";
import { useCommandHandler } from "@/lib/shortcuts/solid";
import { createGestureRecognizer } from "@/lib/gestures/recognizer";
import { attachTouchMouse } from "@/lib/gestures/solid";
import { closestSelector, isEditableTarget, isEditingActive } from "@/lib/gestures/guards";
import { useInteraction } from "@/lib/interaction/solid";

const EXIT_ANIMATION_DURATION = 250;
const ENTRANCE_ANIMATION_DEFER_MS = 260;

// Carousel / gesture tuning. Conservative for smooth low-end devices.
const SWIPE_TRANSITION_MS = 240;
const SWIPE_DECIDE_PX = 8;
const COMMIT_THRESHOLD_PX = 80;
const COMMIT_THRESHOLD_RATIO = 0.22;
const RUBBER_BAND_FACTOR = 0.3;
const COMMIT_FLING_MAX_DURATION_MS = 300;
const COMMIT_FLING_MIN_DISTANCE_PX = 40;
const COMMIT_FLING_AXIS_RATIO = 2;
const SWIPE_FALLBACK_WIDTH_PX = 320;
const SWIPE_FALLBACK_HEIGHT_PX = 900;
const SWIPE_ENTRANCE_RESET_BUFFER_MS = 60;
const SWIPE_RESET_BUFFER_MS = 40;
const VERTICAL_COMMIT_THRESHOLD_PX = 220;
const SWIPE_INDICATOR_TRIGGER_PX = 5;
const HORIZONTAL_SWIPE_INDICATOR_TRIGGER_PX = 10;
const INACTIVE_PANEL_OPACITY = 0.3;

// Track the number of active details drawer components in the application.
// This allows us to detect when we are navigating between tasks while the
// drawer is already open, allowing us to skip entrance animations and avoid flickering.
let activeDrawerCount = 0;

/**
 * The error "Type 'RouterCore' does not satisfy the constraint 'AnyRoute'" occurred because
 * RegisteredRouter refers to the Router instance itself, while RoutePaths expects the
 * Route tree definition. We fix this by accessing RegisteredRouter["routeTree"].
 */
interface TodoDetailRouteViewProps<T extends RoutePaths<RegisteredRouter["routeTree"]>> {
  todoId: string;
  backTo: T;
  backParams?: NavigateOptions<RegisteredRouter, string, T>["params"];
}

const PANEL_CLASS =
  "absolute top-0 bottom-0 w-full overflow-y-auto overflow-x-hidden [scrollbar-gutter:stable] p-6 pb-[calc(1.5rem+var(--safe-area-inset-bottom))]";

export function TodoDetailRouteView<T extends RoutePaths<RegisteredRouter["routeTree"]>>(
  props: TodoDetailRouteViewProps<T>,
) {
  const { t } = useI18n();
  const navigate = useNavigate();
  const drizzle = useDrizzle();
  const interaction = useInteraction();
  const [isOpen, setIsOpen] = createSignal(activeDrawerCount > 0);
  const [animationCompleted, setAnimationCompleted] = createSignal(activeDrawerCount > 0);

  const { navigators, entranceDirection, setEntranceDirection } = useDrawerSwipeNav();

  const prevTodoId = () => navigators()?.getPrevTodoId(props.todoId) ?? null;
  const nextTodoId = () => navigators()?.getNextTodoId(props.todoId) ?? null;

  interface PanelState {
    key: number;
    position: () => number;
    setPosition: (v: number) => void;
    todoId: () => string | null;
    setTodoId: (v: string | null) => void;
    isActive: () => boolean;
    setIsActive: (v: boolean) => void;
  }

  const createPanel = (i: number): PanelState => {
    const [position, setPosition] = createSignal(i - 1);
    const [todoId, setTodoId] = createSignal<string | null>(null);
    const [isActive, setIsActive] = createSignal(false);
    return {
      key: i,
      position,
      setPosition,
      todoId,
      setTodoId,
      isActive,
      setIsActive,
    };
  };

  const panels: [PanelState, PanelState, PanelState] = [
    createPanel(0),
    createPanel(1),
    createPanel(2),
  ];

  // Sincronizar las señales de los paneles fijos cuando cambia la tarea activa
  createEffect(() => {
    const currentId = props.todoId;
    const prevId = prevTodoId();
    const nextId = nextTodoId();

    untrack(() => {
      // Buscar si el nuevo ID ya está cargado en alguno de los paneles
      const currentActivePanel = panels.find((p) => p.todoId() === currentId);

      if (currentActivePanel) {
        // La tarea ya está precargada. Simplemente reordenamos las posiciones y los estados.
        for (const p of panels) {
          const id = p.todoId();
          if (id === currentId) {
            p.setPosition(0);
            p.setIsActive(true);
          } else if (id === prevId) {
            p.setPosition(-1);
            p.setIsActive(false);
          } else if (id === nextId) {
            p.setPosition(1);
            p.setIsActive(false);
          } else {
            // Este panel es el que rota. Se mueve al lado libre.
            const hasPrev = panels.some((x) => x.todoId() === prevId);
            const hasNext = panels.some((x) => x.todoId() === nextId);
            if (!hasPrev) {
              p.setTodoId(prevId);
              p.setPosition(-1);
              p.setIsActive(false);
            } else if (!hasNext) {
              p.setTodoId(nextId);
              p.setPosition(1);
              p.setIsActive(false);
            }
          }
        }
      } else {
        // Carga inicial o navegación discontinua: asignamos los paneles de forma directa.
        panels[0].setTodoId(prevId);
        panels[0].setPosition(-1);
        panels[0].setIsActive(false);

        panels[1].setTodoId(currentId);
        panels[1].setPosition(0);
        panels[1].setIsActive(true);

        panels[2].setTodoId(nextId);
        panels[2].setPosition(1);
        panels[2].setIsActive(false);
      }
    });
  });

  // Carousel translate state.
  const [swipeTx, setSwipeTx] = createSignal(0);
  const [swipeAnimating, setSwipeAnimating] = createSignal(false);
  const [isSwiping, setIsSwiping] = createSignal(false);
  const [swipeTy, setSwipeTy] = createSignal(0);
  const [swipeYAnimating, setSwipeYAnimating] = createSignal(false);
  const [isSwipingY, setIsSwipingY] = createSignal(false);
  const isPastThreshold = () => swipeTy() > VERTICAL_COMMIT_THRESHOLD_PX * RUBBER_BAND_FACTOR;
  const canGoPrev = () => navigators()?.hasPrev() ?? false;
  const canGoNext = () => navigators()?.hasNext() ?? false;
  let viewportRef: HTMLDivElement | undefined = undefined;
  const viewportWidth = () =>
    viewportRef?.clientWidth ??
    (typeof globalThis.innerWidth === "number" ? globalThis.innerWidth : SWIPE_FALLBACK_WIDTH_PX);
  const horizontalCommitThreshold = () =>
    Math.max(COMMIT_THRESHOLD_PX, viewportWidth() * COMMIT_THRESHOLD_RATIO);
  let currentPanelRef: HTMLDivElement | undefined = undefined;
  let prevPanelRef: HTMLDivElement | undefined = undefined;
  let nextPanelRef: HTMLDivElement | undefined = undefined;
  let swipeCommitTimer: ReturnType<typeof setTimeout> | undefined = undefined;

  // Data layer: warm the cache for the centered todo (+ subtasks/attachments)
  // and the two adjacent todos so swipe previews are ready. See the hook.
  const { todoQuery } = useTodoDetailPrefetch({
    todoId: () => props.todoId,
    prevTodoId,
    nextTodoId,
  });

  // Each panel keeps its own scroll position; reset whenever its todoId changes
  // so navigating always lands at the top of the new todo.
  createEffect(() => {
    void props.todoId;
    if (currentPanelRef) {
      currentPanelRef.scrollTop = 0;
    }
  });
  createEffect(() => {
    void prevTodoId();
    if (prevPanelRef) {
      prevPanelRef.scrollTop = 0;
    }
  });
  createEffect(() => {
    void nextTodoId();
    if (nextPanelRef) {
      nextPanelRef.scrollTop = 0;
    }
  });

  const handleClose = () => {
    setIsOpen(false);
    setTimeout(() => {
      void navigate({
        to: props.backTo,
        params: props.backParams,
      });
    }, EXIT_ANIMATION_DURATION);
  };

  // ── Drawer-level shortcuts ─────────────────────────────────────────────
  useCommandHandler(COMMAND_IDS.DRAWER_TOGGLE_IMPORTANT, () => {
    const { data } = todoQuery;
    const dbInstance = drizzle();
    if (!data || !dbInstance) {
      return;
    }
    void updateTodoMutation(dbInstance, data.id, {
      important: data.important === 1 ? 0 : 1,
    });
  });
  useCommandHandler(COMMAND_IDS.DRAWER_SAVE_CLOSE, () => {
    if (!isOpen()) {
      return;
    }
    handleClose();
  });
  // DRAWER_ADD_TO_MY_DAY / DRAWER_MOVE_TO_LIST are reserved — no default
  // binding yet; users can assign them later via the bindings editor.

  const cancelPendingCommit = () => {
    if (swipeCommitTimer) {
      clearTimeout(swipeCommitTimer);
      swipeCommitTimer = undefined;
    }
  };

  /**
   * Arrow-key entrance: navigation already happened so `props.todoId` (and the
   * adjacent IDs) are already the NEW values. We jump the track to the OPPOSITE
   * side without animation so the panel-prev / panel-next (which now hold the
   * OLD current as their content) sits centered, then animate the track back to
   * 0 — mirroring the swipe motion.
   */
  const playEntranceFromDirection = (dir: "next" | "prev") => {
    cancelPendingCommit();
    const width = viewportWidth();
    setSwipeAnimating(false);
    setSwipeTx(dir === "next" ? width : -width);
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        setSwipeAnimating(true);
        setSwipeTx(0);
        swipeCommitTimer = setTimeout(() => {
          setSwipeAnimating(false);
          swipeCommitTimer = undefined;
        }, SWIPE_TRANSITION_MS + SWIPE_ENTRANCE_RESET_BUFFER_MS);
      });
    });
  };

  createEffect(() => {
    const dir = entranceDirection();
    if (!dir) {
      return;
    }
    setEntranceDirection(null);
    untrack(() => {
      playEntranceFromDirection(dir);
    });
  });

  const commitSwipe = (direction: "next" | "prev") => {
    const nav = navigators();
    if (!nav) {
      setSwipeAnimating(true);
      setSwipeTx(0);
      return;
    }
    const width = viewportWidth();
    const target = direction === "next" ? -width : width;
    setSwipeAnimating(true);
    setSwipeTx(target);
    cancelPendingCommit();
    swipeCommitTimer = setTimeout(() => {
      batch(() => {
        if (direction === "next") {
          nav.next();
        } else {
          nav.prev();
        }
        setEntranceDirection(null);
        setSwipeAnimating(false);
        setSwipeTx(0);
      });
      swipeCommitTimer = undefined;
    }, SWIPE_TRANSITION_MS);
  };

  const handleHorizontalRelease = (dx: number, dy: number, duration: number) => {
    const nav = navigators();
    const width = viewportWidth();
    const distanceCommit =
      Math.abs(dx) > Math.max(COMMIT_THRESHOLD_PX, width * COMMIT_THRESHOLD_RATIO);
    const flingCommit =
      duration < COMMIT_FLING_MAX_DURATION_MS &&
      Math.abs(dx) > COMMIT_FLING_MIN_DISTANCE_PX &&
      Math.abs(dx) > Math.abs(dy) * COMMIT_FLING_AXIS_RATIO;
    const shouldCommit = distanceCommit || flingCommit;
    const wantsNext = dx < 0;
    const canCommit = shouldCommit && Boolean(nav) && (wantsNext ? nav?.hasNext() : nav?.hasPrev());

    if (canCommit) {
      commitSwipe(wantsNext ? "next" : "prev");
    } else {
      setSwipeAnimating(true);
      setSwipeTx(0);
      cancelPendingCommit();
      swipeCommitTimer = setTimeout(() => {
        setSwipeAnimating(false);
        swipeCommitTimer = undefined;
      }, SWIPE_TRANSITION_MS + SWIPE_RESET_BUFFER_MS);
    }
  };

  const handleVerticalRelease = (dx: number, dy: number, duration: number) => {
    const isFlingCommit =
      duration < COMMIT_FLING_MAX_DURATION_MS &&
      dy > COMMIT_FLING_MIN_DISTANCE_PX &&
      dy > Math.abs(dx) * COMMIT_FLING_AXIS_RATIO;

    const isDistanceCommit = swipeTy() > VERTICAL_COMMIT_THRESHOLD_PX * RUBBER_BAND_FACTOR;

    setSwipeYAnimating(true);

    if (isFlingCommit || isDistanceCommit) {
      setSwipeTy(
        typeof globalThis.innerHeight === "number"
          ? globalThis.innerHeight
          : SWIPE_FALLBACK_HEIGHT_PX,
      );
      handleClose();
    } else {
      setSwipeTy(0);
      setTimeout(() => {
        setSwipeYAnimating(false);
      }, SWIPE_TRANSITION_MS);
    }
  };

  // Carousel gesture: one recognizer drives both the horizontal todo-to-todo
  // swipe and the vertical pull-to-close. Physics (deadzone, axis-lock, fling)
  // come from the shared recognizer; the panel/commit logic stays here.
  const swipeRecognizer = createGestureRecognizer({
    axis: "both",
    thresholdPx: SWIPE_DECIDE_PX,
    canStart: ({ target }) => {
      if (!isOpen()) {
        return false;
      }
      const el = target instanceof Text ? target.parentElement : target;
      const focusedEditable = isEditableTarget(target) && document.activeElement === el;
      if (focusedEditable || closestSelector(target, '[data-no-swipe="true"]')) {
        return false;
      }
      cancelPendingCommit();
      return true;
    },
    canEngage: (axis, sample) => {
      if (axis === "x") {
        return true;
      }
      if (isEditingActive()) {
        return false;
      }
      const scrollable = currentPanelRef;
      return sample.dy > 0 && Boolean(scrollable) && (scrollable?.scrollTop ?? 1) <= 1;
    },
    onStart: (sample) => {
      if (sample.axis === "x") {
        setIsSwiping(true);
        setSwipeAnimating(false);
      } else {
        setIsSwipingY(true);
        setSwipeYAnimating(false);
      }
    },
    onMove: (sample) => {
      if (sample.axis === "x") {
        const nav = navigators();
        let effective = sample.dx;
        if (nav) {
          if (sample.dx < 0 && !nav.hasNext()) {
            effective = sample.dx * RUBBER_BAND_FACTOR;
          } else if (sample.dx > 0 && !nav.hasPrev()) {
            effective = sample.dx * RUBBER_BAND_FACTOR;
          }
        } else {
          effective = sample.dx * RUBBER_BAND_FACTOR;
        }
        setSwipeTx(effective);
      } else {
        setSwipeTy(sample.dy * RUBBER_BAND_FACTOR);
      }
    },
    onEnd: (sample) => {
      if (sample.axis === "x") {
        handleHorizontalRelease(sample.dx, sample.dy, sample.duration);
      } else {
        handleVerticalRelease(sample.dx, sample.dy, sample.duration);
      }
    },
    onCancel: () => {
      setIsSwiping(false);
      setIsSwipingY(false);
      setSwipeTx(0);
      setSwipeTy(0);
    },
  });

  onMount(() => {
    if (!isOpen()) {
      setTimeout(() => {
        setIsOpen(true);
      }, 16);
    }

    // Wait for the entry slide-in to fully complete (250ms + tiny buffer)
    // before allowing the complex detail content to render.
    setTimeout(() => {
      setAnimationCompleted(true);
    }, ENTRANCE_ANIMATION_DEFER_MS);

    activeDrawerCount += 1;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !interaction.get("dialog.open")) {
        handleClose();
      }
    };

    globalThis.addEventListener("keydown", handleKeyDown);

    onCleanup(() => {
      activeDrawerCount = Math.max(0, activeDrawerCount - 1);
      globalThis.removeEventListener("keydown", handleKeyDown);
      cancelPendingCommit();
    });
  });

  const drawerTransition = () => {
    if (swipeYAnimating()) {
      return `transform ${SWIPE_TRANSITION_MS}ms cubic-bezier(0.32, 0.72, 0, 1)`;
    }
    if (isOpen()) {
      return "none";
    }
    return undefined;
  };

  const asideTransform = () =>
    isOpen() ? `translate3d(0, ${swipeTy()}px, 0)` : `translate3d(100%, ${swipeTy()}px, 0)`;

  const trackStyle = () => ({
    transform: `translate3d(${swipeTx()}px, 0, 0)`,
    transition: swipeAnimating()
      ? `transform ${SWIPE_TRANSITION_MS}ms cubic-bezier(0.32, 0.72, 0, 1)`
      : "none",
    "will-change": isSwiping() || swipeAnimating() ? "transform" : "auto",
  });

  const panelStyle = (panel: PanelState) => {
    const pos = panel.position();
    const tx = swipeTx();
    const width = viewportWidth();

    let opacity = INACTIVE_PANEL_OPACITY;
    if (pos === 0) {
      if (tx === 0) {
        opacity = 1;
      } else {
        const progress = Math.min(1, Math.abs(tx) / width);
        opacity = 1 - (1 - INACTIVE_PANEL_OPACITY) * progress;
      }
    } else {
      const isIncoming = (pos === 1 && tx < 0) || (pos === -1 && tx > 0);
      if (isIncoming) {
        const progress = Math.min(1, Math.abs(tx) / width);
        opacity = INACTIVE_PANEL_OPACITY + (1 - INACTIVE_PANEL_OPACITY) * progress;
      }
    }

    return {
      left: `${pos * 100}%`,
      opacity,
      transition: swipeAnimating()
        ? `opacity ${SWIPE_TRANSITION_MS}ms cubic-bezier(0.32, 0.72, 0, 1)`
        : "none",
      "will-change": isSwiping() || swipeAnimating() ? "opacity" : "auto",
    };
  };

  return (
    <Portal>
      <Transition name="drawer-right">
        <Show when={isOpen()}>
          <div
            class="fixed inset-0 z-[110] overflow-hidden"
            classList={{ "swiping-y": isSwipingY() || swipeYAnimating() }}
          >
            <button
              type="button"
              onClick={handleClose}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  handleClose();
                }
              }}
              class="todo-drawer-backdrop absolute inset-0 bg-black/60 backdrop-blur-[3px] border-none p-0 w-full h-full cursor-default"
              // eslint-disable-next-line lingui/no-unlocalized-strings -- TODO(i18n)
              aria-label="Close details"
            />

            <aside
              ref={(el) => {
                const detach = attachTouchMouse(el, swipeRecognizer);
                onCleanup(detach);
              }}
              id="todo-details-drawer"
              class="absolute right-0 top-0 bottom-0 flex w-full flex-col border-l border-border/20 bg-background/95 backdrop-blur-3xl shadow-[0_0_50px_rgba(0,0,0,0.5)] sm:max-w-md md:max-w-lg will-change-[transform,opacity]"
              style={{
                transform: asideTransform(),
                transition: drawerTransition(),
              }}
            >
              <div class="flex h-full flex-col">
                {/* Grabber indicator zone */}
                <div
                  class="flex flex-col items-center pt-[calc(0.5rem+var(--safe-area-inset-top))] select-none shrink-0"
                  style={{ "touch-action": "none" }}
                >
                  <div
                    class="h-1 w-12 rounded-full transition-all duration-300"
                    classList={{
                      "bg-muted-foreground/30": !isPastThreshold(),
                      "bg-primary w-16 h-1.5": isPastThreshold(),
                    }}
                  />

                  <div
                    class="overflow-hidden transition-all duration-300 flex items-center justify-center w-full"
                    style={{
                      height: swipeTy() > SWIPE_INDICATOR_TRIGGER_PX ? "20px" : "0px",
                      opacity: swipeTy() > SWIPE_INDICATOR_TRIGGER_PX ? 1 : 0,
                      "margin-top": swipeTy() > SWIPE_INDICATOR_TRIGGER_PX ? "4px" : "0px",
                    }}
                  >
                    <div
                      class="flex items-center gap-1.5 text-[10px] font-semibold tracking-wide transition-all duration-300"
                      classList={{
                        "text-muted-foreground": !isPastThreshold(),
                        "text-primary scale-105": isPastThreshold(),
                      }}
                    >
                      <span
                        class="inline-block transition-transform duration-300 text-xs"
                        style={{
                          transform: isPastThreshold() ? "rotate(180deg)" : "rotate(0deg)",
                        }}
                      >
                        ↓
                      </span>
                      <span>
                        {isPastThreshold() ? t(msg`Release to close`) : t(msg`Pull down to close`)}
                      </span>
                    </div>
                  </div>
                </div>

                <div
                  class="flex min-h-14 h-auto items-center justify-between border-b border-border/10 px-6 pb-3 pt-2 shrink-0 select-none"
                  style={{ "touch-action": "none" }}
                >
                  <h2 class="text-lg font-semibold">{t(msg`Task Details`)}</h2>
                  <div class="flex items-center gap-2" data-no-swipe="true">
                    <kbd
                      class="h-4 inline-flex items-center justify-center rounded border border-border/60 bg-background px-1.5 font-mono text-[8px] font-bold text-foreground/80 shadow-sm select-none cursor-help"
                      title={t(msg`Close panel: Press ESC to close this details drawer`)}
                    >
                      ESC
                    </kbd>
                    <button
                      type="button"
                      onClick={handleClose}
                      title={t(msg`Close details`)}
                      class="rounded-lg p-2 text-muted-foreground/80 hover:bg-muted/10 hover:text-foreground active:scale-95 transition-all duration-200 cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                    >
                      <X class="h-5 w-5" />
                    </button>
                  </div>
                </div>
                <div
                  ref={(el) => {
                    viewportRef = el;
                  }}
                  class="flex-1 relative overflow-hidden"
                  style={{ "touch-action": "pan-y" }}
                >
                  {/* Left horizontal swipe commit indicator */}
                  <Show
                    when={
                      isSwiping() &&
                      swipeTx() > HORIZONTAL_SWIPE_INDICATOR_TRIGGER_PX &&
                      canGoPrev()
                    }
                  >
                    <div
                      class="absolute left-4 top-1/2 -translate-y-1/2 z-20 flex items-center gap-2 rounded-full border px-3 py-1.5 shadow-lg bg-background/95 backdrop-blur-md transition-all duration-300 pointer-events-none"
                      classList={{
                        "border-border/30 text-muted-foreground scale-100":
                          swipeTx() <= horizontalCommitThreshold(),
                        "border-primary/50 text-primary bg-primary/5 scale-105 shadow-primary/10":
                          swipeTx() > horizontalCommitThreshold(),
                      }}
                    >
                      <span class="inline-block transition-transform duration-300 text-sm animate-pulse">
                        ←
                      </span>
                      <span class="text-[10px] font-semibold tracking-wide">
                        {swipeTx() > horizontalCommitThreshold()
                          ? t(msg`Release to go back`)
                          : t(msg`Swipe right to go back`)}
                      </span>
                    </div>
                  </Show>

                  {/* Right horizontal swipe commit indicator */}
                  <Show
                    when={
                      isSwiping() &&
                      swipeTx() < -HORIZONTAL_SWIPE_INDICATOR_TRIGGER_PX &&
                      canGoNext()
                    }
                  >
                    <div
                      class="absolute right-4 top-1/2 -translate-y-1/2 z-20 flex items-center gap-2 rounded-full border px-3 py-1.5 shadow-lg bg-background/95 backdrop-blur-md transition-all duration-300 pointer-events-none"
                      classList={{
                        "border-border/30 text-muted-foreground scale-100":
                          Math.abs(swipeTx()) <= horizontalCommitThreshold(),
                        "border-primary/50 text-primary bg-primary/5 scale-105 shadow-primary/10":
                          Math.abs(swipeTx()) > horizontalCommitThreshold(),
                      }}
                    >
                      <span class="text-[10px] font-semibold tracking-wide">
                        {Math.abs(swipeTx()) > horizontalCommitThreshold()
                          ? t(msg`Release for next`)
                          : t(msg`Swipe left for next`)}
                      </span>
                      <span class="inline-block transition-transform duration-300 text-sm animate-pulse">
                        →
                      </span>
                    </div>
                  </Show>

                  <div class="absolute inset-0" style={trackStyle()}>
                    <For each={panels}>
                      {(panel) => (
                        <Show when={panel.todoId()}>
                          {(id) => (
                            <div
                              ref={(el) => {
                                createEffect(() => {
                                  const pos = panel.position();
                                  if (pos === -1) {
                                    prevPanelRef = el;
                                  } else if (pos === 0) {
                                    currentPanelRef = el;
                                  } else if (pos === 1) {
                                    nextPanelRef = el;
                                  }
                                });
                              }}
                              class={PANEL_CLASS}
                              style={panelStyle(panel)}
                              aria-hidden={panel.position() === 0 ? undefined : "true"}
                            >
                              <Suspense fallback={<TodoDetailSkeleton />}>
                                <TodoDetail
                                  todoId={id()}
                                  isActive={panel.isActive()}
                                  onDelete={handleClose}
                                  isEntranceCompleted={animationCompleted()}
                                />
                              </Suspense>
                            </div>
                          )}
                        </Show>
                      )}
                    </For>
                  </div>
                </div>
              </div>
            </aside>
          </div>
        </Show>
      </Transition>
    </Portal>
  );
}
